import type { FC, ReactNode } from "react";

import type { IConfigurationOptions } from "app/pages/digital-marketing/generative-ai/llm-tracker/homepage/hooks/useConfigurationOptions";
import type {
    ExplicitLLModel,
    IAiTrackerCampaign,
    ILanguage,
    IRawCompetitor,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import type { ICountryObject } from "app/services/CountryService";
import type { IWebsiteItem } from "pro-features/components/AutocompleteWebsites/types";

export interface IAiTrackerTableRow extends IAiTrackerCampaign {
    topicCount: number;
    languageName: string;
    canEdit: boolean;
    canDelete: boolean;
    isOwner: boolean;
    isFailed: boolean;
    isCompleted: boolean;
    isLocked: boolean;
}

export enum TabFilter {
    ALL = 0,
    MY = 1,
    SHARED = 2,
    DEMO = 3,
}

export enum MenuItemType {
    Edit = "edit",
    Subscribe = "subscribe",
    Delete = "delete",
}

export type DropdownOnClickProps = { id: MenuItemType };
export const TabFilterNaming = Object.keys(TabFilter);

export interface IStepData {
    brandNames: string[];
    domains: IWebsiteItem[];
    competitors: IRawCompetitor[];
    selectedCountry: ICountryObject;
    selectedLanguage: ILanguage;
    suggestedTopics: string[];
    selectedTopics: string[];
    promptsPerTopic: number;
    selectedModels: ExplicitLLModel[];
    shouldGenerateCompetitors: boolean;
    generatedCompetitorsError: Error | null;
    includeSubdomains: boolean;
    trackerConfigurationsData: IConfigurationOptions;
    isLoadingCompetitors: boolean;
    isLoading: boolean;
    isDetectingBrands: boolean;
}

export type StepErrorField = "brand" | "domain" | "topic" | "competitor.domain" | "competitor.brand";

export interface IStepError {
    field: StepErrorField;
    index?: number;
    message: string;
}

export interface IStepConfig {
    validate?: (data: IStepData) => IStepError[];
    onStepChange?: (data: IStepData, handlers: IStepHandlers) => void;
    component: () => ReactNode;
    renderFooter?: (data: IStepData) => ReactNode;
}

export interface IStepHandlers {
    handleBrandsChange: (brands: string[]) => void;
    handleDomainsChange: (domains: IWebsiteItem[]) => void;
    handleCountriesChange: (countries: ICountryObject) => void;
    handleLanguageChange: (language: ILanguage) => void;
    handleCompetitorChange: (index: number, competitor: IRawCompetitor) => void;
    handleCompetitorDelete: (index: number) => void;
    handleSetCompetitors: (competitors: IRawCompetitor[]) => void;
    handlePromptPerTopicChange: (promptsPerTopic: number) => void;
    handleRemoveTopic: (topic: string) => void;
    handleTopicAdded: (topic: string) => void;
    handleSuggestionAccepted: (topic: string) => void;
    handleSuggestionReturned: (topic: string) => void;
    handleModelsChange: (models: ExplicitLLModel[]) => void;
    handleSubdomainsChange: (include: boolean) => void;
    openPromptsEditModal: () => void;
}

export interface IFormStepContextValue {
    currentStep: number;
    isFirstStep: boolean;
    isLastStep: boolean;
    goToNext: () => void;
    goToPrevious: () => void;
    goToStep: (step: number) => void;
    stepFooterComponent: FC | null;
    stepComponent: () => ReactNode;
    stepData: IStepData;
    stepHandlers: IStepHandlers;
    stepErrors: IStepError[];
    showStepErrors: boolean;
    dismissStepErrors: () => void;
}

export interface ISetupPromptsTopic {
    topicName: string;
    prompts: Array<{
        query: string;
        intent: string;
    }>;
}

export interface ISetupPromptsResponse {
    topics: ISetupPromptsTopic[];
}
